"use client";

import { c as _c } from "react/compiler-runtime";
import { faDownload, faExternalLink } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Col, Container, Dropdown, Row } from "react-bootstrap";
import { Tooltip } from "react-tooltip";
import useDownloader from "react-use-downloader";
import { NextGenToken } from "@/entities/INextgen";
import { numberWithCommas } from "@/helpers/Helpers";
import DotLoader, { Spinner } from "@/components/dotLoader/DotLoader";
export enum Resolution {
  "Thumbnail" = "Thumbnail",
  "0.5K" = "0.5K",
  "1K" = "1K",
  "2K" = "2K",
  "4K" = "4K",
  "8K" = "8K",
  "16K" = "16K",
}
function getUrl(token: NextGenToken, resolution: Resolution) {
  let u = token.image_url;
  if (resolution == Resolution["1K"] && token.collection_id !== 1 || resolution == Resolution["2K"] && token.collection_id == 1) {
    return u;
  }
  if (resolution === Resolution["Thumbnail"]) {
    u = u.replace("/png/", `/thumbnail/`);
  } else {
    u = u.replace("/png/", `/png${resolution.toLowerCase()}/`);
  }
  return u;
}
type NextGenTokenProps = Readonly<{
  token: NextGenToken;
  resolution: Resolution;
}>;
function useImageChecker(token, resolution) {
  const $ = _c(8);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageExists, setImageExists] = useState(false);
  const [imageSize, setImageSize] = useState(0);
  let t0;
  let t1;
  if ($[0] !== resolution || $[1] !== token) {
    t0 = () => {
      const url = getUrl(token, resolution);
      fetch(url, {
        method: "HEAD"
      }).then(response => {
        if (response.ok) {
          setImageExists(true);
          const contentLength = response.headers.get("content-length");
          if (contentLength) {
            const sizeBytes = parseInt(contentLength, 10);
            setImageSize(Math.round(sizeBytes / 1000000 * 100) / 100);
          }
        } else {
          setImageExists(false);
        }
        setImageLoaded(true);
      }).catch(() => setImageExists(false));
    };
    t1 = [token, resolution];
    $[0] = resolution;
    $[1] = token;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== imageExists || $[5] !== imageLoaded || $[6] !== imageSize) {
    t2 = {
      imageLoaded,
      imageExists,
      imageSize
    };
    $[4] = imageExists;
    $[5] = imageLoaded;
    $[6] = imageSize;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function NextGenTokenDownloadDropdownItem(props) {
  const $ = _c(13);
  const {
    imageExists,
    imageSize
  } = useImageChecker(props.token, props.resolution);
  const downloader = useDownloader();
  const t0 = !imageExists;
  let t1;
  if ($[0] !== downloader || $[1] !== imageExists || $[2] !== props.resolution || $[3] !== props.token) {
    t1 = () => {
      if (imageExists) {
        downloader.download(getUrl(props.token, props.resolution), `${props.token.id}_${props.resolution.toUpperCase()}.png`);
      }
    };
    $[0] = downloader;
    $[1] = imageExists;
    $[2] = props.resolution;
    $[3] = props.token;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== imageExists || $[6] !== imageSize) {
    t2 = imageExists && imageSize > 0 ? ` (${numberWithCommas(imageSize)} MB)` : " Coming Soon";
    $[5] = imageExists;
    $[6] = imageSize;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props.resolution || $[9] !== t0 || $[10] !== t1 || $[11] !== t2) {
    t3 = <Dropdown.Item key={props.resolution} disabled={t0} onClick={t1}>{props.resolution}{t2}</Dropdown.Item>;
    $[8] = props.resolution;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
export default function NextGenTokenDownload(props) {
  const $ = _c(22);
  const {
    imageLoaded,
    imageExists,
    imageSize
  } = useImageChecker(props.token, props.resolution);
  let t0;
  if ($[0] !== props.token) {
    t0 = function printResolution(quality) {
      return <span className="d-flex gap-3 align-items-center no-wrap"><FontAwesomeIcon data-tooltip-id={`external-link-${props.token.id}-${quality}`} style={{
          cursor: "pointer",
          height: "20px",
          width: "20px"
        }} onClick={() => {
          const h = getUrl(props.token, quality);
          window.open(h, "_blank");
        }} icon={faExternalLink} /><Tooltip id={`external-link-${props.token.id}-${quality}`} place="top" delayShow={100} style={{
          backgroundColor: "#1F2937",
          color: "white",
          padding: "4px 8px"
        }}>Open in new tab</Tooltip><NextGenTokenDownloadButton token={props.token} quality={quality} /></span>;
    };
    $[0] = props.token;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const printResolution = t0;
  let t1;
  if ($[2] !== imageExists || $[3] !== imageLoaded || $[4] !== printResolution || $[5] !== props.resolution) {
    t1 = function getDisplay() {
      if (!imageLoaded) {
        return <DotLoader />;
      }
      if (!imageExists) {
        return "Coming Soon";
      }
      return printResolution(props.resolution);
    };
    $[2] = imageExists;
    $[3] = imageLoaded;
    $[4] = printResolution;
    $[5] = props.resolution;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const getDisplay = t1;
  let t2;
  if ($[7] !== props.resolution) {
    t2 = <span>{props.resolution}</span>;
    $[7] = props.resolution;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== imageExists || $[10] !== imageSize) {
    t3 = imageExists && imageSize > 0 && ` (${numberWithCommas(imageSize)} MB)`;
    $[9] = imageExists;
    $[10] = imageSize;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t2 || $[13] !== t3) {
    t4 = <Col xs={4}><span className="no-wrap">{t2}{t3}</span></Col>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== getDisplay) {
    t5 = getDisplay();
    $[15] = getDisplay;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t5) {
    t6 = <Col>{t5}</Col>;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t4 || $[20] !== t6) {
    t7 = <Container className="no-padding pt-1 pb-1 "><Row className="d-flex flex-wrap align-items-center">{t4}{t6}</Row></Container>;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
function NextGenTokenDownloadButton(props) {
  const $ = _c(16);
  const downloader = useDownloader();
  if (downloader.isInProgress) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <Spinner />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  const t0 = `download-${props.token.id}-${props.quality}`;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      cursor: "pointer",
      height: "20px",
      width: "20px"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== downloader || $[3] !== props.quality || $[4] !== props.token) {
    t2 = () => {
      downloader.download(getUrl(props.token, props.quality), `${props.token.id}_${props.quality.toUpperCase()}.png`);
    };
    $[2] = downloader;
    $[3] = props.quality;
    $[4] = props.token;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props.class || $[7] !== t0 || $[8] !== t2) {
    t3 = <FontAwesomeIcon data-tooltip-id={t0} icon={faDownload} className={props.class} style={t1} onClick={t2} />;
    $[6] = props.class;
    $[7] = t0;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = `download-${props.token.id}-${props.quality}`;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      backgroundColor: "#1F2937",
      color: "white",
      padding: "4px 8px"
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4) {
    t6 = <Tooltip id={t4} place="top" delayShow={100} style={t5}>Download</Tooltip>;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t6) {
    t7 = <>{t3}{t6}</>;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}